'use client'

import { useState } from 'react'

import { SmartTooltip } from '@/components/extension/smart-tooltip'
import { type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CheckIcon } from 'lucide-react'
import Image from 'next/image'

interface CopyButtonProps extends ButtonProps {
  value: string
  onCopied?: () => void
  className?: string
}

export function CopyButton({
  value,
  onCopied,
  className,
  ...props
}: CopyButtonProps) {
  const [hasCopied, setHasCopied] = useState(false)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setHasCopied(true)
      onCopied?.()

      setTimeout(() => {
        setHasCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy text: ', error)
    }
  }

  return (
    <SmartTooltip content='Copy' side='top' align='center' delayDuration={200}>
      <button
        className={cn(
          'flex items-center gap-1 font-criteria text-xs leading-4.5 text-primary',
          className
        )}
        onClick={copyToClipboard}
        {...props}
      >
        <span className='sr-only'>
          {hasCopied ? 'Copied' : 'Copy to clipboard'}
        </span>
        {hasCopied ? (
          <CheckIcon className='h-4 w-4 text-green-500' />
        ) : (
          <Image
            src={'/icons/icon-copy.svg'}
            alt='icon copy'
            width={16}
            height={16}
          />
        )}
        {hasCopied ? 'Copied' : 'Copy'}
      </button>
    </SmartTooltip>
  )
}
